import React, { useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

type Props = Omit<SelectProps, 'options' | 'onSearch' | 'filterOption' | 'showSearch'> & {
  value?: string | string[] | null;
};

/**
 * Employee picker for recruiters, interview panels and task owners.
 * Uses the recruit-scoped lookup so TA users do not need `hr.view`.
 */
const RecruitPeoplePicker: React.FC<Props> = ({ value, mode, placeholder, ...rest }) => {
  const [search, setSearch] = useState('');
  const selectedIds = useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []).filter(Boolean) as string[],
    [value]
  );

  const { data: found = [], isFetching } = useQuery({
    queryKey: hrmsKeys.recruitPeople(search),
    queryFn: () => hrmsService.recruitPeople({ q: search || undefined }),
    staleTime: 60_000,
  });
  const { data: selected = [] } = useQuery({
    queryKey: [...hrmsKeys.recruitPeople('ids'), selectedIds.join(',')],
    queryFn: () => hrmsService.recruitPeople({ ids: selectedIds.join(',') }),
    enabled: selectedIds.length > 0,
    staleTime: 300_000,
  });

  const options = useMemo(() => {
    const map = new Map<string, any>();
    [...selected, ...found].forEach((p: any) => map.set(p.id, p));
    return [...map.values()].map((p: any) => ({
      value: p.id,
      label: `${p.full_name}${p.employee_number ? ` (${p.employee_number})` : ''}`,
      title: p.department_name || undefined,
    }));
  }, [found, selected]);

  return (
    <Select
      {...rest}
      mode={mode}
      value={value as any}
      allowClear
      showSearch
      filterOption={false}
      onSearch={setSearch}
      notFoundContent={isFetching ? <Spin size="small" /> : 'No employees'}
      placeholder={placeholder || 'Search employee'}
      options={options}
    />
  );
};

export default RecruitPeoplePicker;
